// owner: matching-domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import { EpochMsSchema, UlidSchema } from './common';

export const MATCHING_MODES = ['auto', 'embeddings', 'lexical'] as const;
/** Value of the MATCHING_MODE env var. */
export const MatchingModeSchema = z.enum(MATCHING_MODES);
export type MatchingMode = z.infer<typeof MatchingModeSchema>;

export const MatchMethodSchema = z.enum(['embeddings', 'lexical']);
export const MatchEvidenceSchema = z.object({
  matchedSkills: z.array(z.string()),
  missingSkills: z.array(z.string()),
});
export const MatchFactorsSchema = z.object({
  semantic: z.number().nullable(),
  skills: z.number().nullable(),
  fit: z.number().nullable(),
});

/** Which scorer actually ran, and why. Surfaced on every match response. */
export const MatchingCapabilitySchema = z.object({
  requested: MatchingModeSchema,
  resolved: MatchMethodSchema,
  /** null when not checked (MATCHING_MODE=lexical never probes Workers AI). */
  embeddingsAvailable: z.boolean().nullable(),
  /** Why embeddings were unavailable (probe failure), when relevant. */
  detail: z.string().nullable(),
});

const MatchCore = {
  score: z.number().int().min(0).max(100),
  method: MatchMethodSchema,
  factors: MatchFactorsSchema,
  evidence: MatchEvidenceSchema,
  reasons: z.array(z.string()),
};

export const MatchQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  minScore: z.coerce.number().min(0).max(100).default(0),
});

export const MatchIdParamSchema = z.object({ id: UlidSchema });

export const JobMatchSchema = z.object({
  jobId: UlidSchema,
  slug: z.string(),
  title: z.string(),
  org: z.object({ slug: z.string(), name: z.string() }),
  location: z.string(),
  remote: z.enum(['onsite', 'hybrid', 'remote']),
  ...MatchCore,
});
export const JobsForMeSchema = z.object({
  capability: MatchingCapabilitySchema,
  computedAt: EpochMsSchema,
  items: z.array(JobMatchSchema),
});

export const CandidateMatchSchema = z.object({
  userId: UlidSchema,
  displayName: z.string(),
  handle: z.string(),
  headline: z.string(),
  location: z.string(),
  hasApplied: z.boolean(),
  ...MatchCore,
});
export const CandidatesForJobSchema = z.object({
  capability: MatchingCapabilitySchema,
  jobId: UlidSchema,
  computedAt: EpochMsSchema,
  items: z.array(CandidateMatchSchema),
});
